// ./src/hooks/useVenueCoordinates.ts
//
// A venue's coordinates — its primary address's (else its first address's) latitude and longitude, or nulls. The
// accommodations drawer uses them to suggest each stay's distance from the venue; the Accommodations page and the
// wizard's Accommodations step (#62 P6(f)) both read them here, so the two cannot disagree about where the venue is.
//
// ⓘ Best-effort: a failed read or an address without coordinates leaves both null, and the drawer simply suggests no
//   distance. A different venue starts from null, never from the previous one's point.

import { useEffect, useState } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { Venue, VenueService } from "@/utils/api";

export interface VenueCoordinates {
  latitude: number | null;
  longitude: number | null;
}

const NONE: VenueCoordinates = { latitude: null, longitude: null };

export const useVenueCoordinates = (venueId: string | null | undefined): VenueCoordinates => {
  const [coords, setCoords] = useState<VenueCoordinates>(NONE);

  useEffect(() => {
    let cancelled = false;

    setCoords(NONE);

    if (!venueId) {
      return undefined;
    }

    VenueService.get(venueId)
      .then((res) => {
        if (cancelled || res.code !== OP_RESULT_CODES.OK) {
          return;
        }

        const venue = (res.data as Venue[] | undefined)?.[0];
        const addr = venue?.primaryAddress ?? venue?.addresses?.[0];

        if (typeof addr?.latitude === "number" && typeof addr?.longitude === "number") {
          setCoords({ latitude: addr.latitude, longitude: addr.longitude });
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [venueId]);

  return coords;
};
